import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import {
  selectActiveCompany,
  selectPageLoading,
  selectQueueFormId,
} from "../../../core/data/redux/leads-queue/selectors";
import LeadsQueueHeader from "./components/leads-in-queue/LeadsQueueHeader";
import LeadBankTab from "./components/leads-in-queue/LeadBankTab";
import Loader from "../../../core/common/loader";
import LeadsQueueFooter from "./components/leads-in-queue/LeadsQueueFooter";
import { fetchLeadBankForms, fetchLeadsQueueData } from "../../../core/data/redux/leads-queue/thunks";
import { exportLeadBankLeads } from "../../../core/data/redux/services/leadsQueueApi";
import { notify } from "../../../services/toast.config";

import type { Employee, LeadBankFilters, LeadBankForm, LeadBankQuery } from "../../../core/data/redux/leads-queue/types";

import { ALL_FORMS_VALUE } from "../../../core/data/redux/leads-queue/constants";

const toLeadBankQuery = (filters: LeadBankFilters): LeadBankQuery => ({
  facebookFormId:
    filters.sourceFilter === "WhatsApp" || filters.facebookFormId === ALL_FORMS_VALUE
      ? null
      : filters.facebookFormId,
  leadSourceChild: filters.sourceFilter || null,
  search: filters.search,
});

const LeadBankPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const activeCompany = useSelector(selectActiveCompany);
  const formId = useSelector(selectQueueFormId);
  const pageLoading = useSelector(selectPageLoading);

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [bankForms, setBankForms] = useState<LeadBankForm[]>([]);
  const [filters, setFilters] = useState<LeadBankFilters | null>(null);
  const [isExportStarted, setIsExportStarted] = useState(false);

  useEffect(() => {
    // We just fetch queue data with limit 1 to grab the employees associated with the queue form,
    // which is needed for the manual assignment drop-down.
    if (activeCompany.company_id && formId) {
       dispatch(fetchLeadsQueueData({
        companyId: activeCompany.company_id,
        companyType: activeCompany.company_type,
        sourceType: "all",
        sourceFilter: { property: null, operator: "is", value: "" },
        dateRange: { start_date: null, end_date: null },
        formId,
        page: 1,
        limit: 1,
        search: ""
      })).unwrap().then(data => {
        setEmployees(data.employees || []);
      }).catch(() => {});
    }
  }, [activeCompany.company_id, activeCompany.company_type, formId]);

  const loadBankForms = useCallback(async (): Promise<LeadBankForm[]> => {
    try {
      const forms = await dispatch(fetchLeadBankForms({
        companyId: activeCompany.company_id,
        companyType: activeCompany.company_type,
      })).unwrap();
      setBankForms(forms);
      return forms;
    } catch {
      setBankForms([]);
      return [];
    }
  }, [dispatch, activeCompany.company_id, activeCompany.company_type]);

  useEffect(() => {
    if (!activeCompany.company_id) return;
    setFilters(null);
    loadBankForms().then((forms) => {
      setFilters({
        sourceFilter: "",
        facebookFormId: forms[0]?.form_id ?? ALL_FORMS_VALUE,
        search: "",
      });
    });
  }, [activeCompany.company_id, activeCompany.company_type, loadBankForms]);

  const handleRefreshForms = async () => {
    const forms = await loadBankForms();
    setFilters((prev) => {
      if (!prev || prev.facebookFormId === ALL_FORMS_VALUE) return prev;
      if (forms.some((f) => f.form_id === prev.facebookFormId)) return prev;
      return { ...prev, facebookFormId: forms[0]?.form_id ?? ALL_FORMS_VALUE };
    });
  };

  const handleExport = async () => {
    if (!filters || isExportStarted) return;
    try {
      notify.success("Download Started");
      setIsExportStarted(true);
      const signature = localStorage.getItem("signature");
      const query = toLeadBankQuery(filters);
      const blob = await exportLeadBankLeads(
        signature,
        activeCompany.company_id,
        activeCompany.company_type,
        query.facebookFormId,
        query.leadSourceChild,
        query.search,
      );
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Lead_Bank_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed", error);
      notify.error("Failed to export Lead Bank leads");
    } finally {
      setIsExportStarted(false);
    }
  };

  return (
    <>
      {pageLoading && <Loader />}
      <div className="page-wrapper">
        <div className="content">
          <LeadsQueueHeader
            isExportStarted={isExportStarted}
            handleExport={handleExport}
            activeView="lead_bank"
          />

          <LeadBankTab
            employees={employees}
            bankForms={bankForms}
            filters={filters}
            query={filters ? toLeadBankQuery(filters) : null}
            onFiltersChange={setFilters}
            onRefreshForms={handleRefreshForms}
          />
        </div>
        <LeadsQueueFooter />
      </div>
    </>
  );
};
export default LeadBankPage;
